import {
  AccessTimeOutlined,
  Attachment,
  FolderOutlined,
  LockOutlined,
  PushPinOutlined,
} from '@mui/icons-material';
import { CardActionArea, CardContent, styled, Tooltip, Typography } from '@mui/material';
import React, { forwardRef, useCallback } from 'react';
import Highlighter from 'react-highlight-words';

import { CardSize, ToolName } from '../../Types/enums';
import { createMessageSnippet } from '../../util/textUtils';
import timeSince from '../../util/timeUitls';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import ToolIcons from '../ToolIcons';

const CardContainer = styled('div')<{ selected: boolean; size: CardSize }>(
  ({ selected, size }) => ({
    width: size === CardSize.SMALL ? '300px' : size === CardSize.MEDIUM ? '430px' : '100%',
    ':hover': {
      cursor: 'pointer',
      border: '1px solid #F900D3',
    },
    minHeight: size === CardSize.SMALL ? '95px' : '120px',
    borderRadius: '4px',
    WebkitTapHighlightColor: 'transparent',
    display: 'block',
    WebkitTextDecoration: 'none',
    textDecoration: 'none',
    border: '1px solid #CED7E6',
    backgroundColor: selected ? '#E8EEF8' : 'white',
    boxSizing: 'border-box',
    position: 'relative',
    color: '#070154',
    padding: '10px 10px 0px 10px',
  })
);

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '0px !important',
  minHeight: '50px',
  height: '100%',
});

const StyledCardActionArea = styled(CardActionArea)({
  ':hover': {
    '.MuiCardActionArea-focusHighlight': {
      opacity: 0,
    },
  },
});

const IconButtonContainer = styled('div')({
  right: '0px',
  top: '0px',
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
});

type CardProps = {
  // Shared
  type: string;
  title: string;
  selected: boolean;
  pinned: boolean;
  matchingMessage?: string;
  searchQuery: string;
  CardContextMenu: React.ReactNode;
  icon?: React.ReactNode;
  onCardClick: () => void;
  size?: CardSize;

  // Chat specific
  time?: string;
  folderName?: string;
  hasFileUploaded?: boolean;
  isPrivate?: boolean;
  tools?: ToolName[];
};

const Card = (
  {
    type,
    title,
    selected,
    pinned,
    matchingMessage,
    searchQuery,
    size = CardSize.MEDIUM,

    time,
    folderName,
    hasFileUploaded = false,
    isPrivate,
    onCardClick,
    CardContextMenu,
    icon,
    tools = [],
  }: CardProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const typeToCapital = useCallback(() => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }, [type]);

  return (
    <CardContainer onClick={onCardClick} selected={selected} size={size}>
      <StyledCardContent ref={ref}>
        <IconButtonContainer>
          <StyledCardActionArea>
            <div style={{ display: 'flex', gap: '5px' }}>
              {icon && <>{icon}</>}
              <AutoTruncatingTypographyWithTooltip
                text={title}
                searchQuery={searchQuery}
                variant={size === CardSize.SMALL ? 'caption1' : 'body1'}
                sx={{
                  fontWeight: '400',
                  fontSize: '14px',
                  ...(size === CardSize.SMALL ? { lineHeight: '16px' } : {}),
                }}
                useHighlighter={true}
              />
            </div>
          </StyledCardActionArea>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'default',
            }}
          >
            {pinned && <PushPinOutlined sx={{ color: '#070154', height: '16px', width: '16px' }} />}
            {CardContextMenu}
          </div>
        </IconButtonContainer>
        <div>
          {matchingMessage && (
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
              <Highlighter
                highlightClassName="highlight"
                searchWords={[searchQuery]}
                autoEscape={true}
                textToHighlight={createMessageSnippet(
                  matchingMessage,
                  searchQuery,
                  size === CardSize.SMALL ? 45 : 140
                )}
              />
            </Typography>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '5px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: size === CardSize.SMALL ? '15px' : '30px',
                }}
              >
                {time && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {size !== CardSize.SMALL && (
                      <AccessTimeOutlined
                        sx={{ width: '16px', height: '16px', color: '#757575' }}
                      />
                    )}
                    <Typography variant="caption1" sx={{ color: '#757575', fontSize: '12px' }}>
                      {timeSince(new Date(time))}
                    </Typography>
                  </div>
                )}
                {folderName && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FolderOutlined sx={{ width: '16px', height: '16px', color: '#757575' }} />
                    <Typography variant="caption1" sx={{ color: '#757575' }}>
                      {size === CardSize.SMALL
                        ? folderName.length > 25
                          ? folderName.substring(0, 22) + '...'
                          : folderName
                        : folderName.length > 35
                        ? folderName.substring(0, 32) + '...'
                        : folderName}
                    </Typography>
                  </div>
                )}
              </div>
              {hasFileUploaded && (
                <Tooltip title={`${typeToCapital()} has uploaded files`}>
                  <Attachment sx={{ fontSize: '1.2rem', color: '#757575' }} />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {(tools.length > 0 || isPrivate) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <ToolIcons tools={tools} size={16} />

            {isPrivate && (
              <Tooltip title="This conversation is private">
                <LockOutlined sx={{ width: '16px', height: '16px', color: '#070154' }} />
              </Tooltip>
            )}
          </div>
        )}
      </StyledCardContent>
    </CardContainer>
  );
};

Card.displayName = 'Card';

export default forwardRef(Card);
